import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MAX_ATTACHMENTS, MAX_LABEL, MAX_MEDIA_BYTES, audioAttachment, chatAudioType, locationAttachment, prepareImage,
  recordAttachment,
} from '../../utils/chat/attachments';
import { formatDuration } from '../../utils/audioNotes';
import VoiceNoteRecorder from '../VoiceNoteRecorder';
import GalleryPicker from './GalleryPicker';
import PinPicker from './PinPicker';
import * as S from '../specimens/styles';

/*
 * Writing a message (routes/chat.py; docs/FIELD_AI_VISION_SPEC.md 34): text,
 * and what it carries --
 *
 *   Attach    a photograph taken now (the phone's camera), from the Field
 *             gallery (a sent record by reference, an unsent photograph as a
 *             copy), or files (photographs and voice notes)
 *   Location  here (this device's fix, with its accuracy), a pin on the
 *             map, or a record's place -- each sent once, on purpose
 *   voice     hold to record (VoiceNoteRecorder), or tap to start and stop
 *
 * Photographs are made ready here: at most 2048 px, re-encoded by a canvas
 * (no EXIF, so no GPS), with a 320 px thumbnail (utils/chat/attachments.js).
 * `allowed` is the kinds this person may attach (the server's `can.attach`:
 * an event's guest sends photographs and voice notes only).
 * onSend({body, attachments}) takes the message; the outbox does the rest.
 */
export default function Composer({ canSend, allowed = [], me, onSend }) {
  const { t } = useTranslation(['chat', 'common']);
  const [text, setText] = useState('');
  const [drafts, setDrafts] = useState([]);
  const [menu, setMenu] = useState(null);       // 'attach' | 'place' | null
  const [dialog, setDialog] = useState(null);   // 'gallery' | 'recordPlace' | 'pin' | null
  const [working, setWorking] = useState(false);
  const [note, setNote] = useState(null);       // { tone: 'warn' | 'status', text }
  const camera = useRef(null);
  const files = useRef(null);
  const may = (kind) => allowed.includes(kind);
  const room = MAX_ATTACHMENTS - drafts.length;

  const add = (items) => {
    const kept = items.filter(Boolean);
    if (!kept.length) return;
    if (kept.length > room) setNote({ tone: 'warn', text: t('attach.tooMany', { max: MAX_ATTACHMENTS }) });
    setDrafts((list) => [...list, ...kept.slice(0, Math.max(0, MAX_ATTACHMENTS - list.length))]);
  };

  const addFiles = async (picked, { copy = false } = {}) => {
    setMenu(null);
    setNote(null);
    const list = [...(picked || [])];
    if (!list.length) return;
    setWorking(true);
    const made = [];
    for (const file of list) {
      const type = String(file.type || '').toLowerCase();
      if (type.startsWith('audio/') || chatAudioType(type, file.name)) {
        const audio = audioAttachment({ blob: file, contentType: chatAudioType(type, file.name) });
        if (!audio) setNote({ tone: 'warn', text: t('attach.audioType') });
        else if (file.size > MAX_MEDIA_BYTES) setNote({ tone: 'warn', text: t('attach.tooLarge') });
        else made.push(audio);
        continue;
      }
      try {
        made.push(await prepareImage(file, { copy }));
      } catch {
        setNote({ tone: 'warn', text: t('attach.notAPicture') });
      }
    }
    setWorking(false);
    add(made);
  };

  const here = () => {
    setMenu(null);
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setNote({ tone: 'warn', text: t('attach.noFix') });
      return;
    }
    setNote({ tone: 'status', text: t('attach.findingFix') });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setNote(null);
        add([locationAttachment({
          lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy_m: pos.coords.accuracy,
          captured_at: new Date(pos.timestamp || Date.now()).toISOString(), source: 'device',
        })]);
      },
      () => setNote({ tone: 'warn', text: t('attach.noFix') }),
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 30000 },
    );
  };

  const voice = (notes) => {
    const made = (notes || []).map((n) => audioAttachment(n, { recordedVia: 'hold_button' }));
    if (made.some((m) => !m)) setNote({ tone: 'warn', text: t('attach.audioType') });
    add(made);
  };

  const label = (id, value) => setDrafts((list) => list.map((d) => (d.id === id
    ? { ...d, location: { ...d.location, label: value.slice(0, MAX_LABEL) || null } } : d)));
  const drop = (id) => setDrafts((list) => list.filter((d) => d.id !== id));

  const submit = () => {
    const body = text.trim();
    if ((!body && !drafts.length) || working) return;
    const attachments = drafts.map((d) => (d.kind === 'location'
      ? { ...d, location: { ...d.location, label: (d.location.label || '').replace(/\s+/g, ' ').trim() || null } } : d));
    setText('');
    setDrafts([]);
    setNote(null);
    onSend({ body, attachments });
  };

  return (
    <div style={{ borderTop: '1px solid var(--c-border)', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {drafts.length > 0 && (
        <ul style={draftList} aria-label={t('attach.drafts')} data-testid="chat-drafts">
          {drafts.map((d) => (
            <Draft key={d.id} draft={d} onRemove={() => drop(d.id)} onLabel={(v) => label(d.id, v)} />
          ))}
        </ul>
      )}
      {drafts.some((d) => d.copy) && <p style={S.hint}>{t('attach.copyNotice')}</p>}
      {note && (
        <p role={note.tone === 'warn' ? 'alert' : 'status'} style={note.tone === 'warn' ? S.error : S.hint}
          data-testid="chat-compose-note">{note.text}</p>
      )}
      {canSend && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {(may('image') || may('record_ref')) && (
            <button type="button" style={S.ghostBtn} aria-expanded={menu === 'attach'} disabled={!room || working}
              onClick={() => setMenu((m) => (m === 'attach' ? null : 'attach'))} data-testid="chat-attach"
              title={t('attach.tip.attach')}>{working ? t('attach.preparing') : t('attach.attach')}</button>
          )}
          {may('location') && (
            <button type="button" style={S.ghostBtn} aria-expanded={menu === 'place'} disabled={!room}
              onClick={() => setMenu((m) => (m === 'place' ? null : 'place'))} data-testid="chat-place"
              title={t('attach.tip.place')}>{t('attach.location')}</button>
          )}
          {may('audio') && room > 0 && (
            <VoiceNoteRecorder value={[]} onChange={voice} hold compact capSeconds={300} testId="chat-voice"
              disabled={working} />
          )}
        </div>
      )}
      {menu === 'attach' && (
        <div role="group" aria-label={t('attach.attach')} style={menuRow}>
          {may('image') && (
            <button type="button" style={S.ghostBtn} onClick={() => camera.current?.click()} data-testid="chat-attach-camera">
              {t('attach.camera')}
            </button>
          )}
          {may('record_ref') && (
            <button type="button" style={S.ghostBtn} onClick={() => { setMenu(null); setDialog('gallery'); }}
              data-testid="chat-attach-gallery">{t('attach.fromGallery')}</button>
          )}
          {may('image') && (
            <button type="button" style={S.ghostBtn} onClick={() => files.current?.click()} data-testid="chat-attach-files">
              {t('attach.files')}
            </button>
          )}
        </div>
      )}
      {menu === 'place' && (
        <div role="group" aria-label={t('attach.location')} style={menuRow}>
          <button type="button" style={S.ghostBtn} onClick={here} data-testid="chat-place-here">{t('attach.here')}</button>
          <button type="button" style={S.ghostBtn} onClick={() => { setMenu(null); setDialog('pin'); }}
            data-testid="chat-place-pin">{t('attach.pin')}</button>
          <button type="button" style={S.ghostBtn} onClick={() => { setMenu(null); setDialog('recordPlace'); }}
            data-testid="chat-place-record">{t('attach.recordPlace')}</button>
        </div>
      )}
      {/* The camera: the phone's own, for a photograph taken now. */}
      <input ref={camera} type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
        tabIndex={-1} aria-hidden="true" data-testid="chat-camera-file"
        onChange={(e) => { const picked = e.target.files; addFiles(picked).finally(() => { e.target.value = ''; }); }} />
      <input ref={files} type="file" multiple style={{ display: 'none' }} tabIndex={-1} aria-hidden="true"
        accept={`image/*${may('audio') ? ',audio/webm,audio/mp4,audio/x-m4a,.m4a,.webm' : ''}`}
        data-testid="chat-files"
        onChange={(e) => { const picked = e.target.files; addFiles(picked).finally(() => { e.target.value = ''; }); }} />
      <form onSubmit={(e) => { e.preventDefault(); submit(); }} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={4000}
          disabled={!canSend} placeholder={canSend ? t('writeHint') : t('readOnlyHint')}
          aria-label={drafts.length ? t('attach.caption') : t('message')}
          data-testid="chat-composer" style={{ ...S.input, flex: 1, resize: 'vertical' }} dir="auto"
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }} />
        <button type="submit" style={S.btn()} disabled={!canSend || working || (!text.trim() && !drafts.length)}
          data-testid="chat-send">{t('send')}</button>
      </form>
      {dialog === 'gallery' && (
        <GalleryPicker me={me} mode="attach" onClose={() => setDialog(null)}
          onPickRecord={(r) => add([recordAttachment(r)])}
          onPickCopies={(blobs) => addFiles(blobs, { copy: true })} />
      )}
      {dialog === 'recordPlace' && (
        <GalleryPicker me={me} mode="place" onClose={() => setDialog(null)}
          onPickPlace={(p) => add([locationAttachment({ ...p, source: 'record' })])} />
      )}
      {dialog === 'pin' && (
        <PinPicker onClose={() => setDialog(null)}
          onPick={(p) => add([locationAttachment({ ...p, source: 'map_pin' })])} />
      )}
    </div>
  );
}

/** One attachment waiting in the composer, with a way to take it out. */
function Draft({ draft, onRemove, onLabel }) {
  const { t } = useTranslation('chat');
  const url = useObjectUrl(draft.kind === 'image' ? (draft.thumb || draft.blob) : null);
  return (
    <li style={draftItem} data-testid="chat-draft" data-kind={draft.kind}>
      {draft.kind === 'image' && (url ? <img src={url} alt={t('attach.aPhoto')} style={draftThumb} />
        : <span role="img" aria-label={t('attach.aPhoto')}>📷</span>)}
      {draft.kind === 'audio' && (
        <span>🎙️ {Number.isFinite(draft.duration_s) ? <span dir="ltr">{formatDuration(draft.duration_s)}</span> : t('attach.voiceNote')}</span>
      )}
      {draft.kind === 'location' && (
        <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span>📍 <span dir="ltr">{draft.location.lat.toFixed(5)}, {draft.location.lng.toFixed(5)}</span>
            {Number.isFinite(draft.location.accuracy_m) && <> · {t('attach.accuracy', { m: Math.round(draft.location.accuracy_m) })}</>}
          </span>
          <input value={draft.location.label || ''} onChange={(e) => onLabel(e.target.value)} maxLength={MAX_LABEL}
            placeholder={t('attach.labelHint')} aria-label={t('attach.label')} style={{ ...S.input, padding: '2px 6px' }}
            dir="auto" data-testid="chat-draft-label" />
        </span>
      )}
      {draft.kind === 'record_ref' && <span dir="auto">🔗 {draft.label || t('attach.aCollection')}</span>}
      <button type="button" onClick={onRemove} style={removeButton} aria-label={t('attach.remove')}
        title={t('attach.remove')} data-testid="chat-draft-remove">×</button>
    </li>
  );
}

function useObjectUrl(blob) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (!blob || typeof URL.createObjectURL !== 'function') return undefined;
    const made = URL.createObjectURL(blob);
    setUrl(made);
    return () => URL.revokeObjectURL?.(made);
  }, [blob]);
  return url;
}

const draftList = { listStyle: 'none', margin: 0, padding: 0, display: 'flex', gap: 8, flexWrap: 'wrap' };
const draftItem = {
  display: 'flex', alignItems: 'center', gap: 6, padding: 4, border: '1px solid var(--c-border)', borderRadius: 8,
  background: 'var(--c-bg-subtle)', fontSize: 13, maxWidth: '100%',
};
const draftThumb = { width: 48, height: 48, objectFit: 'cover', borderRadius: 6 };
const removeButton = {
  minWidth: 32, minHeight: 32, border: '1px solid var(--c-border-strong)', borderRadius: 6, background: 'var(--c-surface)',
  color: 'var(--c-text-fg)', cursor: 'pointer', fontSize: 16,
};
const menuRow = { display: 'flex', gap: 8, flexWrap: 'wrap' };
